/**
 * OrphansCard — list of dangling references (records missing a
 * downstream link). Uncapped and edge-complete: every sub-100% edge in
 * the coverage chain produces matching findings here (both derive from
 * the same link facts). The list scrolls when long — never truncates.
 * Each row: warning icon, type + target, descriptor + step number.
 *
 * Pure presentation; consumes `data.orphans`.
 */
import React from 'react';
import {
  TypographyH2,
  TypographyMuted,
  TypographySmall,
} from '../../../../components/ui/Typography';
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
} from '../../../../components/ui/Empty';
import { Icon } from '../../../../components/ui/Icon';
import type { AggregatedOverviewData, OrphanFinding } from '../../aggregate/types';
import styles from './OrphansCard.module.css';

interface OrphansCardProps {
  data: AggregatedOverviewData;
}

const TYPE_LABEL: Record<OrphanFinding['type'], string> = {
  asset: 'Asset',
  scenario: 'Damage Scenario',
  threat: 'Threat',
  attack_path: 'Attack Path',
  risk: 'Risk',
  goal: 'Goal',
  requirement: 'Requirement',
};

export default function OrphansCard({ data }: OrphansCardProps) {
  const items = data.orphans;

  if (items.length === 0) {
    return (
      <div className={`${styles.card} ${styles.cardClean}`}>
        <div className={styles.cardHeader}>
          <TypographyH2 className={styles.cardTitle}>Orphans — No findings</TypographyH2>
        </div>
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No orphans</EmptyTitle>
            <EmptyDescription>Every record in scope has its downstream link.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <TypographyH2 className={styles.cardTitle}>Orphans — Audit Findings</TypographyH2>
        <TypographyMuted className={styles.cardMeta}>
          {items.length} open
        </TypographyMuted>
      </div>
      <div className={styles.list}>
        {items.map((o) => (
          <div key={o.id} className={styles.row}>
            <span className={styles.icon} aria-hidden="true">
              <Icon name="warning" size="xs" />
            </span>
            <div className={styles.text}>
              <TypographySmall className={styles.title}>
                {TYPE_LABEL[o.type]} <code className={styles.target}>{o.target}</code>
              </TypographySmall>
              <TypographyMuted className={styles.desc}>
                {o.desc} · Step {o.step}
              </TypographyMuted>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
